import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface RefundBody {
  refundAmount:       number;
  expiryDate?:        string;
  paymentRefundDate?: string;
  remarksType?:       'text' | 'image' | 'image and text';
  remarksText?:       string;
  remarksImage?:      string;
}

interface AddRefundArgs extends RefundBody {
  invoiceId: string;
}

interface AddMembershipRefundArgs extends RefundBody {
  membershipId: string;
}

interface AddAddonMembershipRefundArgs extends RefundBody {
  membershipId: string;
}

/* ─── API ────────────────────────────────────────────────────────────── */

// POST /invoices/:id/refund and POST /memberships/:membershipId/refund are both
// confirmed (permission: CREDIT_NOTE_CREATE, same request body shape).
// The DELETE endpoint below is still an unconfirmed guess — correct once known.
export const refundApi = createApi({
  reducerPath: 'refundApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Refund', 'Invoice'] as const,
  endpoints:   (builder) => ({
    addRefund: builder.mutation<unknown, AddRefundArgs>({
      query: ({ invoiceId, ...body }) => ({ url: `/invoices/${invoiceId}/refund`, method: 'POST', body }),
      invalidatesTags: ['Refund', 'Invoice'],
    }),
    addMembershipRefund: builder.mutation<unknown, AddMembershipRefundArgs>({
      query: ({ membershipId, ...body }) => ({ url: `/memberships/${membershipId}/refund`, method: 'POST', body }),
      invalidatesTags: ['Refund', 'Invoice'],
    }),
    // Addon-type memberships refund through a dedicated endpoint/permission
    // (ADDON_MEMBERSHIP_REFUND) instead of the regular membership refund above.
    addAddonMembershipRefund: builder.mutation<unknown, AddAddonMembershipRefundArgs>({
      query: ({ membershipId, ...body }) => ({ url: `/memberships/${membershipId}/refund-addon`, method: 'POST', body }),
      invalidatesTags: ['Refund', 'Invoice'],
    }),
    deleteRefund: builder.mutation<unknown, string>({
      query: (invoiceId) => ({ url: `/invoices/${invoiceId}/refund`, method: 'DELETE' }),
      invalidatesTags: ['Refund', 'Invoice'],
    }),
  }),
});

export const { useAddRefundMutation, useAddMembershipRefundMutation, useAddAddonMembershipRefundMutation, useDeleteRefundMutation } = refundApi;
